import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, type UserRole } from '../AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getSessions, revokeSession, createCustomer, inviteUser, changePassword, getChatbots, getRetentionSettings, updateRetentionSettings, getAnalyticsOverview, getAnalyticsDaily, getEmailDraftStats, getSalesReferenceAccess, updateSalesReferenceAccess, type RetentionSettings, type AnalyticsOverview, type DailyCount, type EmailDraftStats } from '../api';
import BarChart from '../components/BarChart';
import { validatePasswordStrength, isPasswordValid, type PasswordValidation, type ChatbotListItem } from '@neural-intelligence/shared';
import AppHeader from '../components/AppHeader';
import ActivityLogSection from '../components/ActivityLogSection';
import HeaderUtilityActions from '../components/HeaderUtilityActions';

interface Session {
  id: string;
  ip_address: string;
  user_agent: string;
  country: string | null;
  city: string | null;
  created_at: string;
  last_active: string;
}

interface User {
  id: string;
  email: string;
  role: UserRole;
  created_at: string;
  assigned_chatbot_id: string | null;
  chatbot_name: string | null;
}

function AnalyticsDashboard() {
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [dailyData, setDailyData] = useState<DailyCount[]>([]);
  const [emailStats, setEmailStats] = useState<EmailDraftStats | null>(null);
  const [showDashboard, setShowDashboard] = useState(() => localStorage.getItem('ni-show-dashboard') !== 'false');

  useEffect(() => {
    getAnalyticsOverview().then(r => { if (r.success && r.data) setOverview(r.data); });
    getAnalyticsDaily().then(r => { if (r.success && r.data) setDailyData(r.data); });
    getEmailDraftStats().then(r => { if (r.success && r.data) setEmailStats(r.data); });
  }, []);

  if (!overview) return null;

  return (
    <div style={{ background: 'var(--color-bg-white)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: '24px', marginTop: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showDashboard ? '16px' : '0' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--color-text-primary)', margin: 0 }}>Analytics Overview</h2>
        <button
          onClick={() => { const next = !showDashboard; setShowDashboard(next); localStorage.setItem('ni-show-dashboard', String(next)); }}
          className="btn btn-ghost btn-small"
          style={{ padding: '4px 8px', color: 'var(--color-text-muted)' }}
          title={showDashboard ? 'Hide analytics' : 'Show analytics'}
        >
          {showDashboard ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
          )}
        </button>
      </div>
      <div className={`analytics-panel${showDashboard ? ' open' : ''}`}>
        <div className="analytics-layout">
          <div className="analytics-cards">
            {[
              { label: 'Chats (24h)', value: overview.last24h, prev: overview.prev24h, color: 'var(--color-primary)' },
              { label: 'Chats (7d)', value: overview.last7d, prev: overview.prev7d, color: 'var(--color-primary)' },
              { label: 'Chats (30d)', value: overview.last30d, prev: overview.prev30d, color: 'var(--color-primary)' },
              { label: 'Total chats', value: overview.total, prev: null as number | null, color: 'var(--color-primary)' },
              ...(emailStats && emailStats.total > 0 ? [
                { label: 'Drafts (24h)', value: emailStats.last24h, prev: emailStats.prev24h, color: 'var(--color-teal)' },
                { label: 'Drafts (7d)', value: emailStats.last7d, prev: emailStats.prev7d, color: 'var(--color-teal)' },
                { label: 'Drafts (30d)', value: emailStats.last30d, prev: emailStats.prev30d, color: 'var(--color-teal)' },
                { label: 'Total drafts', value: emailStats.total, prev: null as number | null, color: 'var(--color-teal)' },
                ...(emailStats.errors > 0 ? [{ label: 'Errors', value: emailStats.errors, prev: null as number | null, color: 'var(--color-teal)', danger: true }] : []),
              ] : []),
            ].map(({ label, value, prev, color, danger }: { label: string; value: number; prev: number | null; color: string; danger?: boolean }) => {
              const trend = prev !== null && prev > 0 ? Math.round(((value - prev) / prev) * 100) : null;
              return (
                <div key={label} style={{ background: 'var(--color-bg-white)', border: '1px solid var(--color-border)', borderLeft: `3px solid ${color}`, borderRadius: 'var(--radius-md)', padding: '12px 14px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--color-text-muted)', fontWeight: 500, marginBottom: '2px' }}>{label}</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                    <span style={{ fontSize: '24px', fontWeight: 700, color: danger && value > 0 ? 'var(--color-danger)' : 'var(--color-text-primary)' }}>{value.toLocaleString()}</span>
                    {trend !== null && (
                      <span style={{ fontSize: '11px', fontWeight: 600, color: trend >= 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>
                        {trend >= 0 ? '+' : ''}{trend}%
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {dailyData.length > 0 ? (
            <div className="analytics-chart" style={{ background: 'var(--color-bg-primary)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: '20px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-text-primary)', margin: '0 0 4px' }}>Activity (Last 30 Days)</h3>
              <BarChart
                labels={dailyData.map(d => new Date(d.day + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' }))}
                series={[
                  { name: 'Conversations', color: 'var(--color-primary)', data: dailyData.map(d => d.conversations) },
                  ...(dailyData.some(d => d.drafts > 0) ? [{ name: 'Email drafts', color: 'var(--color-teal)', data: dailyData.map(d => d.drafts) }] : []),
                ]}
                height={220}
              />
            </div>
          ) : (
            <div className="analytics-chart" style={{ background: 'var(--color-bg-primary)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)', fontSize: '14px' }}>
              No activity data yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const RETENTION_OPTIONS = [
  { value: 0, label: 'Unlimited (keep all)' },
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
  { value: 180, label: '180 days' },
  { value: 365, label: '1 year' },
];

function SalesReferenceAccessCard() {
  const { toast } = useToast();
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSalesReferenceAccess().then((result) => {
      if (result.success && result.data) {
        setEnabled(result.data.sales_reference_enabled);
      }
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  async function handleToggle(nextValue: boolean) {
    setSaving(true);
    const result = await updateSalesReferenceAccess(nextValue);
    if (result.success) {
      setEnabled(nextValue);
      toast.success(nextValue ? 'Sales reference access enabled for sales users' : 'Sales reference access disabled for sales users');
    } else {
      toast.error(result.error || 'Failed to update sales reference access');
    }
    setSaving(false);
  }

  return (
    <div style={{ background: 'var(--color-bg-white)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: '24px', marginTop: '24px' }}>
      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div style={{ maxWidth: '560px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '4px', color: 'var(--color-text-primary)' }}>Sales Reference Access</h2>
          <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '16px' }}>
            Control whether sales users can open the internal Sales Reference page. Admins always keep access.
          </p>
          <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', margin: 0 }}>
            When off, sales users lose both the header shortcut and direct page access.
          </p>
        </div>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: 'var(--color-bg-white)', border: `2px solid ${enabled ? 'var(--color-success)' : 'var(--color-border)'}`, borderRadius: '8px', cursor: loading || saving ? 'not-allowed' : 'pointer', fontSize: '15px', fontWeight: 500, opacity: loading || saving ? 0.7 : 1, position: 'relative' }}>
          <input
            type="checkbox"
            checked={enabled}
            disabled={loading || saving}
            style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
            onChange={(e) => {
              void handleToggle(e.target.checked);
            }}
          />
          <div style={{ width: '48px', height: '24px', background: enabled ? 'var(--color-success)' : 'var(--color-text-muted)', borderRadius: '12px', transition: 'background 0.2s ease', position: 'relative', opacity: loading || saving ? 0.6 : 1 }}>
            <div style={{ position: 'absolute', top: '2px', left: enabled ? '26px' : '2px', width: '20px', height: '20px', background: 'white', borderRadius: '50%', transition: 'left 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
          </div>
          <span>
            {loading ? 'Loading access setting...' : enabled ? 'Sales access enabled' : 'Sales access disabled'}
          </span>
        </label>
      </div>
    </div>
  );
}

function DataRetentionCard() {
  const { toast } = useToast();
  const [retention, setRetention] = useState<RetentionSettings | null>(null);
  const [convDays, setConvDays] = useState(0);
  const [draftDays, setDraftDays] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getRetentionSettings().then(r => {
      if (r.success && r.data) {
        setRetention(r.data);
        setConvDays(r.data.data_retention_days);
        setDraftDays(r.data.email_draft_retention_days);
      }
    });
  }, []);

  async function handleSave() {
    setSaving(true);
    const result = await updateRetentionSettings({ data_retention_days: convDays, email_draft_retention_days: draftDays });
    if (result.success) {
      toast.success('Retention settings saved');
      // Refresh counts
      const fresh = await getRetentionSettings();
      if (fresh.success && fresh.data) setRetention(fresh.data);
    } else {
      toast.error(result.error || 'Failed to save');
    }
    setSaving(false);
  }

  return (
    <div style={{ background: 'var(--color-bg-white)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: '24px', marginTop: '24px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '4px', color: 'var(--color-text-primary)' }}>Data Retention</h2>
      <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '20px' }}>Configure how long data is kept before automatic cleanup.</p>

      {retention && (
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <div style={{ padding: '12px 16px', background: 'var(--color-bg-primary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', flex: '1 1 140px' }}>
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', fontWeight: 500 }}>Conversations</div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--color-text-primary)' }}>{retention.counts.conversations.toLocaleString()}</div>
          </div>
          <div style={{ padding: '12px 16px', background: 'var(--color-bg-primary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', flex: '1 1 140px' }}>
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', fontWeight: 500 }}>Messages</div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--color-text-primary)' }}>{retention.counts.messages.toLocaleString()}</div>
          </div>
          <div style={{ padding: '12px 16px', background: 'var(--color-bg-primary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', flex: '1 1 140px' }}>
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', fontWeight: 500 }}>Email Drafts</div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--color-text-primary)' }}>{retention.counts.drafts.toLocaleString()}</div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '16px' }}>
        <div>
          <label style={{ fontSize: '14px', fontWeight: 500, color: 'var(--color-text-primary)', display: 'block', marginBottom: '6px' }}>Conversation retention</label>
          <select className="input" value={convDays} onChange={e => setConvDays(Number(e.target.value))} style={{ width: '100%' }}>
            {RETENTION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: '14px', fontWeight: 500, color: 'var(--color-text-primary)', display: 'block', marginBottom: '6px' }}>Email draft retention</label>
          <select className="input" value={draftDays} onChange={e => setDraftDays(Number(e.target.value))} style={{ width: '100%' }}>
            {RETENTION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {(convDays > 0 || draftDays > 0) && (
        <div style={{ padding: '10px 14px', background: 'var(--color-warning-light)', borderRadius: 'var(--radius-md)', fontSize: '13px', color: 'var(--color-badge-warning-text)', marginBottom: '16px' }}>
          Data older than the configured period will be permanently deleted during the daily cleanup (3:00 AM UTC).
        </div>
      )}

      <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving...' : 'Save Retention Settings'}
      </button>
    </div>
  );
}

export default function Settings() {
  const { logout, user } = useAuth();
  const { toast, confirm, removeToast } = useToast();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [dbInfo, setDbInfo] = useState<{ primaryLocation: string; currentDatacenter: string } | null>(null);
  
  // New user form state
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState<'admin' | 'sales' | 'customer'>('sales');
  const [creatingUser, setCreatingUser] = useState(false);
  const [userCreatedSuccess, setUserCreatedSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [chatbotList, setChatbotList] = useState<ChatbotListItem[]>([]);
  const [newUserChatbotId, setNewUserChatbotId] = useState('');
  const [inviteMode, setInviteMode] = useState(true);
  const [customerSearch, setCustomerSearch] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const teamUsers = useMemo(() => users.filter(u => u.role === 'admin' || u.role === 'sales'), [users]);
  const { customerUsers, filteredCustomers, orphanedCustomers, sortedCustomerGroups } = useMemo(() => {
    const customerUsers = users.filter(u => u.role === 'customer');
    const searchLower = customerSearch.toLowerCase();
    const filtered = customerSearch
      ? customerUsers.filter(u => u.email.toLowerCase().includes(searchLower) || (u.chatbot_name && u.chatbot_name.toLowerCase().includes(searchLower)))
      : customerUsers;
    const orphaned = filtered.filter(u => !u.assigned_chatbot_id);
    const assigned = filtered.filter(u => u.assigned_chatbot_id);
    const byBot: Record<string, { chatbotName: string; customers: User[] }> = {};
    for (const u of assigned) {
      const key = u.assigned_chatbot_id!;
      if (!byBot[key]) byBot[key] = { chatbotName: u.chatbot_name || 'Unknown', customers: [] };
      byBot[key].customers.push(u);
    }
    const sorted = Object.entries(byBot).sort((a, b) => a[1].chatbotName.localeCompare(b[1].chatbotName));
    return { customerUsers, filteredCustomers: filtered, orphanedCustomers: orphaned, sortedCustomerGroups: sorted };
  }, [users, customerSearch]);

  const emptyValidation: PasswordValidation = { length: false, uppercase: false, lowercase: false, number: false, special: false };

  // Password validation state
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>(emptyValidation);

  // Reset password state
  const [resetPasswordUserId, setResetPasswordUserId] = useState<string | null>(null);
  const [resetPasswordEmail, setResetPasswordEmail] = useState('');
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetPasswordValidation, setResetPasswordValidation] = useState<PasswordValidation>(emptyValidation);

  // Change own password state
  const [changePwdCurrent, setChangePwdCurrent] = useState('');
  const [changePwdNew, setChangePwdNew] = useState('');
  const [changePwdConfirm, setChangePwdConfirm] = useState('');
  const [changePwdSaving, setChangePwdSaving] = useState(false);
  const [changePwdMessage, setChangePwdMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [changePwdValidation, setChangePwdValidation] = useState<PasswordValidation>(emptyValidation);
  const changePwdValid = isPasswordValid(changePwdValidation);

  async function handleDeleteUser(userId: string, email: string, role: string) {
    // Security check on frontend
    if (email === user?.email) {
      toast.error('Cannot delete your own account');
      return;
    }

    if (role === 'admin') {
      toast.error('Cannot delete admin users');
      return;
    }

    if (!await confirm({ title: 'Delete User', description: `Are you sure you want to delete ${email}? This action cannot be undone.`, confirmLabel: 'Delete', destructive: true })) {
      return;
    }

    const loadingId = toast.loading('Deleting user...');
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/settings/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      const data = await response.json();
      removeToast(loadingId);

      if (data.success) {
        toast.success(`User ${email} deleted successfully!`);
        loadUsers();
      } else {
        toast.error(data.error || 'Failed to delete user');
      }
    } catch (error) {
      removeToast(loadingId);
      toast.error('Failed to delete user. Please try again.');
    }
  }

  async function handleResetPassword(e: React.FormEvent) {
    e.preventDefault();
    
    if (!resetPasswordUserId) return;

    setResettingPassword(true);
    const loadingId = toast.loading('Resetting password...');

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/settings/users/${resetPasswordUserId}/password`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          password: resetPasswordValue,
        }),
      });

      const data = await response.json();

      removeToast(loadingId);
      if (data.success) {
        toast.success(`Password reset successfully for ${resetPasswordEmail}!`);
        setResetPasswordUserId(null);
        setResetPasswordEmail('');
        setResetPasswordValue('');
        setResetPasswordValidation(emptyValidation);
      } else {
        toast.error(data.error || 'Failed to reset password');
      }
    } catch (error) {
      removeToast(loadingId);
      toast.error('Failed to reset password. Please try again.');
    } finally {
      setResettingPassword(false);
    }
  }

  useEffect(() => {
    loadSettings();
    loadSessions();
    loadUsers();
    loadChatbotList();
  }, []);

  async function loadSettings() {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/settings`, {
        credentials: 'include',
      });

      if (response.status === 401) {
        logout();
        return;
      }

      const data = await response.json();
      if (data.success && data.data) {
        if (data.dbInfo) {
          setDbInfo(data.dbInfo);
        }
      }
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }

  async function loadSessions() {
    try {
      const result = await getSessions();
      if (result.success && result.data) {
        setSessions(result.data);
      }
    } catch (error) {
      // Silently fail - sessions are not critical
    }
  }

  async function loadUsers() {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/settings/users`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setUsers(data.data);
        }
      }
    } catch (error) {
      // Silently fail
    }
  }

  async function loadChatbotList() {
    try {
      const result = await getChatbots();
      if (result.success && result.data) setChatbotList(result.data);
    } catch (error) {}
  }

  async function handleRevokeSession(sessionId: string) {
    if (!await confirm({ title: 'Revoke Session', description: 'Are you sure you want to revoke this session? The user will be logged out.', confirmLabel: 'Revoke', destructive: true })) {
      return;
    }

    try {
      const result = await revokeSession(sessionId);
      if (result.success) {
        toast.success('Session revoked successfully');
        loadSessions(); // Reload list
      } else {
        console.error('Revoke session error:', result.error);
        toast.error(result.error || 'Failed to revoke session');
      }
    } catch (error) {
      console.error('Revoke session exception:', error);
      toast.error('Failed to revoke session');
    }
  }

  async function copyCredentialsToClipboard() {
    const credentials = `Email: ${newUserEmail}\nPassword: ${newUserPassword}`;

    try {
      await navigator.clipboard.writeText(credentials);
      toast.success('Credentials copied to clipboard!');
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = credentials;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success('Credentials copied to clipboard!');
      } catch (err) {
        toast.error('Failed to copy credentials');
      }
      document.body.removeChild(textArea);
    }
  }

  function resetCreateUserForm() {
    setShowCreateUser(false);
    setNewUserEmail('');
    setNewUserPassword('');
    setNewUserRole('sales');
    setNewUserChatbotId('');
    setPasswordValidation(emptyValidation);
    setUserCreatedSuccess(false);
    setShowPassword(false);
    setInviteMode(true);
  }

  function closeCreateUserForm() {
    resetCreateUserForm();
  }

  async function handleCreateUser(e: React.FormEvent) {
    e.preventDefault();
    setCreatingUser(true);

    // Invite mode: send invite email instead of creating with password
    if (inviteMode) {
      const loadingId = toast.loading('Sending invite...');
      try {
        if (newUserRole === 'customer' && !newUserChatbotId) {
          removeToast(loadingId);
          toast.error('Please select a chatbot to assign to this customer');
          setCreatingUser(false);
          return;
        }
        const result = await inviteUser({
          email: newUserEmail,
          role: newUserRole,
          ...(newUserChatbotId ? { assigned_chatbot_id: newUserChatbotId } : {}),
        });
        removeToast(loadingId);
        if (result.success) {
          toast.success('Invite sent successfully!');
          resetCreateUserForm();
          loadUsers();
        } else {
          toast.error(result.error || 'Failed to send invite');
        }
      } catch {
        removeToast(loadingId);
        toast.error('Failed to send invite. Check email configuration.');
      } finally {
        setCreatingUser(false);
      }
      return;
    }

    // Manual password mode
    const loadingId = toast.loading('Creating user...');

    try {
      let data: any;

      if (newUserRole === 'customer') {
        if (!newUserChatbotId) {
          removeToast(loadingId);
          toast.error('Please select a chatbot to assign to this customer');
          setCreatingUser(false);
          return;
        }
        const result = await createCustomer({
          email: newUserEmail,
          password: newUserPassword,
          assigned_chatbot_id: newUserChatbotId,
        });
        data = result;
      } else {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/settings/users`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: newUserEmail, password: newUserPassword, role: newUserRole }),
        });
        data = await response.json();
      }

      removeToast(loadingId);
      if (data.success) {
        toast.success('User created successfully!');
        setUserCreatedSuccess(true);
        loadUsers();
      } else {
        toast.error(data.error || 'Failed to create user');
      }
    } catch (error) {
      removeToast(loadingId);
      toast.error('Failed to create user. Please try again.');
    } finally {
      setCreatingUser(false);
    }
  }

  async function handleTestEmail() {
    const recipient = prompt('Enter the email address to send the test email to:');
    if (!recipient) {
      return; // User cancelled
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipient)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // proceed with test email
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/settings/test-email`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ recipient }),
      });

      if (response.status === 401) {
        logout();
        return;
      }

      const data = await response.json();
      if (data.success) {
        toast.success(`Test email sent to ${recipient}! Check the inbox.`);
      } else {
        toast.error(data.error || 'Failed to send test email');
      }
    } catch (error) {
      toast.error('Failed to send test email');
    }
  }

  if (loading) {
    return (
      <div style={{ padding: '20px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <AppHeader actions={<HeaderUtilityActions current="settings" />}>
        <div className="flex flex-center gap-3">
          <Link to="/" className="nav-link nav-link-brand">
            <img src="/logo.png" alt="Neural Intelligence" className="nav-logo" />
            Dashboard
          </Link>
          <span className="breadcrumb-separator">/</span>
          <h1>Settings</h1>
        </div>
      </AppHeader>

      <div style={{ padding: '20px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>

        {/* Change Password Section (all roles) */}
        <div style={{ backgroundColor: 'var(--color-bg-white)', padding: '24px', borderRadius: '8px', boxShadow: 'var(--shadow-sm)', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px', color: 'var(--color-text-primary)' }}>Change Password</h2>
          <form onSubmit={async (e) => {
            e.preventDefault();
            if (!changePwdValid || changePwdNew !== changePwdConfirm) return;
            setChangePwdSaving(true); setChangePwdMessage(null);
            try {
              const res = await changePassword(changePwdCurrent, changePwdNew);
              if (res.success) {
                setChangePwdMessage({ type: 'success', text: 'Password changed successfully. Other sessions have been signed out.' });
                setChangePwdCurrent(''); setChangePwdNew(''); setChangePwdConfirm('');
                setChangePwdValidation({ length: false, uppercase: false, lowercase: false, number: false, special: false });
              } else {
                setChangePwdMessage({ type: 'error', text: res.error || 'Failed to change password' });
              }
            } catch { setChangePwdMessage({ type: 'error', text: 'Failed to change password' }); }
            finally { setChangePwdSaving(false); }
          }}>
            <div style={{ display: 'grid', gap: '16px', maxWidth: '400px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px', color: 'var(--color-text-primary)' }}>Current Password</label>
                <input type="password" value={changePwdCurrent} onChange={(e) => setChangePwdCurrent(e.target.value)} placeholder="Enter current password" required style={{ width: '100%', padding: '10px 14px', border: '2px solid var(--color-border)', borderRadius: '8px', fontSize: '14px', outline: 'none', fontFamily: 'inherit', background: 'var(--color-bg-white)', color: 'var(--color-text-primary)' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px', color: 'var(--color-text-primary)' }}>New Password</label>
                <input type="password" value={changePwdNew} onChange={(e) => { setChangePwdNew(e.target.value); setChangePwdValidation(validatePasswordStrength(e.target.value)); }} placeholder="Enter new password" required style={{ width: '100%', padding: '10px 14px', border: '2px solid var(--color-border)', borderRadius: '8px', fontSize: '14px', outline: 'none', fontFamily: 'inherit', background: 'var(--color-bg-white)', color: 'var(--color-text-primary)' }} />
                {changePwdNew && (
                  <div style={{ marginTop: '8px', padding: '10px 12px', background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: '6px' }}>
                    <p style={{ fontSize: '12px', color: 'var(--color-text-primary)', margin: '0 0 6px 0', fontWeight: 600 }}>Password Requirements</p>
                    <div className="grid-2col" style={{ gap: '4px', fontSize: '12px' }}>
                      {([['length', '12+ characters'], ['number', 'One number'], ['uppercase', 'One uppercase'], ['special', 'One special (!@#-)'], ['lowercase', 'One lowercase']] as const).map(([key, label]) => (
                        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {changePwdValidation[key] ? (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                          ) : (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="3"><circle cx="12" cy="12" r="10" /></svg>
                          )}
                          <span style={{ color: changePwdValidation[key] ? 'var(--color-success)' : 'var(--color-text-muted)' }}>{label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px', color: 'var(--color-text-primary)' }}>Confirm New Password</label>
                <input type="password" value={changePwdConfirm} onChange={(e) => setChangePwdConfirm(e.target.value)} placeholder="Confirm new password" required style={{ width: '100%', padding: '10px 14px', border: `2px solid ${changePwdConfirm && changePwdNew !== changePwdConfirm ? 'var(--color-danger-border)' : 'var(--color-border)'}`, borderRadius: '8px', fontSize: '14px', outline: 'none', fontFamily: 'inherit', background: 'var(--color-bg-white)', color: 'var(--color-text-primary)' }} />
                {changePwdConfirm && changePwdNew !== changePwdConfirm && (
                  <p style={{ fontSize: '12px', color: 'var(--color-danger)', marginTop: '4px' }}>Passwords do not match</p>
                )}
              </div>
            </div>
            {changePwdMessage && (
              <div style={{ marginTop: '12px', padding: '10px 14px', background: changePwdMessage.type === 'success' ? 'var(--color-success-light)' : 'var(--color-danger-light)', border: `1px solid ${changePwdMessage.type === 'success' ? 'var(--color-success-border)' : 'var(--color-danger-border)'}`, borderRadius: '8px', fontSize: '14px', color: changePwdMessage.type === 'success' ? 'var(--color-badge-success-text)' : 'var(--color-danger)', maxWidth: '400px' }}>
                {changePwdMessage.text}
              </div>
            )}
            <button type="submit" disabled={changePwdSaving || !changePwdValid || !changePwdCurrent || changePwdNew !== changePwdConfirm} style={{ marginTop: '16px', padding: '10px 24px', background: changePwdSaving || !changePwdValid || !changePwdCurrent || changePwdNew !== changePwdConfirm ? 'var(--color-secondary)' : 'var(--color-primary)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: changePwdSaving || !changePwdValid || !changePwdCurrent || changePwdNew !== changePwdConfirm ? 'not-allowed' : 'pointer' }}>
              {changePwdSaving ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>

        {/* User Management Section */}
        {user?.role === 'admin' && (<>
        <div
          style={{
            backgroundColor: 'var(--color-bg-white)',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: 'var(--shadow-sm)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600' }}>
              User Management
            </h2>
            <button
              onClick={() => setShowCreateUser(!showCreateUser)}
              style={{
                padding: '8px 16px',
                background: 'var(--color-primary)',
                border: 'none',
                borderRadius: '6px',
                color: 'white',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {showCreateUser ? 'Cancel' : 'Create New User'}
            </button>
          </div>

          {showCreateUser && (
            <div style={{
              background: 'var(--color-bg-white)',
              padding: '32px',
              borderRadius: '12px',
              border: '1px solid var(--color-border)',
              boxShadow: 'var(--shadow-sm)'
            }}>
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--color-text-primary)', margin: '0 0 4px 0' }}>
                  {userCreatedSuccess ? 'User Created' : 'Create New User'}
                </h3>
                <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', margin: 0 }}>
                  {userCreatedSuccess 
                    ? 'Copy these credentials and share them with the new user'
                    : 'Add a new team member with their email, password, and role'
                  }
                </p>
              </div>

              {userCreatedSuccess ? (
                // Success state - show credentials with copy button
                <div>
                  <div style={{
                    background: 'var(--color-success-light)',
                    border: '2px solid var(--color-success-border)',
                    borderRadius: '12px',
                    padding: '16px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--color-badge-success-text)' }}>Account successfully created</div>
                      <div style={{ fontSize: '13px', color: 'var(--color-success)', marginTop: '2px' }}>The user can now log in with these credentials</div>
                    </div>
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '13px', color: 'var(--color-text-primary)' }}>
                      Email Address
                    </label>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <input
                        type="text"
                        value={newUserEmail}
                        readOnly
                        style={{
                          flex: 1,
                          padding: '12px 14px',
                          border: '2px solid var(--color-border)',
                          borderRadius: '8px',
                          fontSize: '14px',
                          background: 'var(--color-bg-secondary)',
                          color: 'var(--color-text-primary)',
                          fontFamily: 'inherit',
                          userSelect: 'none'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '13px', color: 'var(--color-text-primary)' }}>
                      Password
                    </label>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{ flex: 1, position: 'relative' }}>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={newUserPassword}
                          readOnly
                          style={{
                            width: '100%',
                            padding: '12px 48px 12px 14px',
                            border: '2px solid var(--color-border)',
                            borderRadius: '8px',
                            fontSize: '14px',
                            background: 'var(--color-bg-secondary)',
                            color: 'var(--color-text-primary)',
                            fontFamily: showPassword ? 'monospace' : 'inherit',
                            letterSpacing: showPassword ? '0' : '0.1em',
                            userSelect: 'none'
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{
                            position: 'absolute',
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--color-text-secondary)',
                            transition: 'color 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-text-primary)'}
                          onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-text-secondary)'}
                          title={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                              <line x1="1" y1="1" x2="23" y2="23"/>
                            </svg>
                          ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                              <circle cx="12" cy="12" r="3"/>
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                    <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)', margin: '8px 0 0 0', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="16" x2="12" y2="12"/>
                        <line x1="12" y1="8" x2="12.01" y2="8"/>
                      </svg>
                      This password will not be shown again - make sure to save it
                    </p>
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      type="button"
                      onClick={copyCredentialsToClipboard}
                      style={{
                        flex: 1,
                        padding: '12px 24px',
                        background: 'var(--color-primary)',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--color-primary-hover)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--color-primary)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                      </svg>
                      Copy Credentials
                    </button>
                    <button
                      type="button"
                      onClick={closeCreateUserForm}
                      style={{
                        padding: '12px 24px',
                        background: 'var(--color-bg-white)',
                        border: '2px solid var(--color-border)',
                        borderRadius: '8px',
                        color: 'var(--color-text-secondary)',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--color-bg-secondary)';
                        e.currentTarget.style.borderColor = 'var(--color-border)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--color-bg-white)';
                        e.currentTarget.style.borderColor = 'var(--color-border)';
                      }}
                    >
                      Done
                    </button>
                  </div>
                </div>
              ) : (
              <form onSubmit={handleCreateUser}>
                {/* Invite mode toggle */}
                <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', background: 'var(--color-bg-secondary)', borderRadius: '8px', padding: '4px' }}>
                  <button type="button" onClick={() => setInviteMode(true)} style={{ flex: 1, padding: '8px 16px', fontSize: '14px', fontWeight: 600, border: 'none', borderRadius: '6px', cursor: 'pointer', background: inviteMode ? 'var(--color-bg-white)' : 'transparent', color: inviteMode ? 'var(--color-text-primary)' : 'var(--color-text-secondary)', boxShadow: inviteMode ? 'var(--shadow-sm)' : 'none', transition: 'all 0.2s' }}>Send invite email</button>
                  <button type="button" onClick={() => setInviteMode(false)} style={{ flex: 1, padding: '8px 16px', fontSize: '14px', fontWeight: 600, border: 'none', borderRadius: '6px', cursor: 'pointer', background: !inviteMode ? 'var(--color-bg-white)' : 'transparent', color: !inviteMode ? 'var(--color-text-primary)' : 'var(--color-text-secondary)', boxShadow: !inviteMode ? 'var(--shadow-sm)' : 'none', transition: 'all 0.2s' }}>Set password manually</button>
                </div>

                <div className="grid-2col" style={{ gap: '24px', marginBottom: '24px' }}>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '13px', color: 'var(--color-text-primary)' }}>
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={newUserEmail}
                      onChange={(e) => setNewUserEmail(e.target.value)}
                      placeholder="user@example.com"
                      required
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        border: '2px solid var(--color-border)',
                        borderRadius: '8px',
                        fontSize: '14px',
                        transition: 'all 0.2s',
                        outline: 'none',
                        fontFamily: 'inherit'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = 'var(--color-primary)';
                        e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--color-border)';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  {!inviteMode && (<div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '13px', color: 'var(--color-text-primary)' }}>
                      Password
                    </label>
                    <input
                      type="password"
                      value={newUserPassword}
                      onChange={(e) => {
                        setNewUserPassword(e.target.value);
                        setPasswordValidation(validatePasswordStrength(e.target.value));
                      }}
                      placeholder="••••••••••••"
                      required={!inviteMode}
                      minLength={12}
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        border: '2px solid var(--color-border)',
                        borderRadius: '8px',
                        fontSize: '14px',
                        transition: 'all 0.2s',
                        outline: 'none',
                        fontFamily: 'inherit',
                        letterSpacing: '0.05em'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = 'var(--color-primary)';
                        e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--color-border)';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                    <div style={{
                      marginTop: '10px',
                      padding: '14px',
                      background: 'linear-gradient(to bottom, var(--color-bg-secondary), var(--color-bg-secondary))',
                      borderRadius: '8px',
                      border: '1px solid var(--color-border)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="12" y1="16" x2="12" y2="12"/>
                          <line x1="12" y1="8" x2="12.01" y2="8"/>
                        </svg>
                        <p style={{ fontSize: '13px', color: 'var(--color-text-primary)', margin: 0, fontWeight: '600' }}>
                          Password Requirements
                        </p>
                      </div>
                      <div className="grid-2col" style={{ gap: '8px', fontSize: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {passwordValidation.length ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" />
                            </svg>
                          )}
                          <span style={{ color: passwordValidation.length ? 'var(--color-success)' : 'var(--color-text-muted)' }}>12+ characters</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {passwordValidation.number ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" />
                            </svg>
                          )}
                          <span style={{ color: passwordValidation.number ? 'var(--color-success)' : 'var(--color-text-muted)' }}>One number</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {passwordValidation.uppercase ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" />
                            </svg>
                          )}
                          <span style={{ color: passwordValidation.uppercase ? 'var(--color-success)' : 'var(--color-text-muted)' }}>One uppercase</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {passwordValidation.special ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" />
                            </svg>
                          )}
                          <span style={{ color: passwordValidation.special ? 'var(--color-success)' : 'var(--color-text-muted)' }}>One special (!@#-)</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {passwordValidation.lowercase ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" />
                            </svg>
                          )}
                          <span style={{ color: passwordValidation.lowercase ? 'var(--color-success)' : 'var(--color-text-muted)' }}>One lowercase</span>
                        </div>
                      </div>
                    </div>
                  </div>)}

                  {inviteMode && (
                    <div style={{ gridColumn: '1 / -1', padding: '12px 16px', background: 'var(--color-primary-light)', border: '1px solid var(--color-primary-border)', borderRadius: '8px' }}>
                      <p style={{ fontSize: '14px', color: 'var(--color-primary-text)', margin: 0 }}>An email will be sent with a link to set their password. The link expires in 72 hours.</p>
                    </div>
                  )}

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '13px', color: 'var(--color-text-primary)' }}>
                      Role
                    </label>
                    <select
                      value={newUserRole}
                      onChange={(e) => setNewUserRole(e.target.value as 'admin' | 'sales' | 'customer')}
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        border: '2px solid var(--color-border)',
                        borderRadius: '8px',
                        fontSize: '14px',
                        background: 'var(--color-bg-white)',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        outline: 'none',
                        fontFamily: 'inherit'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = 'var(--color-primary)';
                        e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--color-border)';
                        e.target.style.boxShadow = 'none';
                      }}
                    >
                      <option value="sales">Sales — Read-only access</option>
                      <option value="admin">Admin — Full access</option>
                      <option value="customer">Customer — Single chatbot access</option>
                    </select>
                    <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)', margin: '8px 0 0 0' }}>
                      {newUserRole === 'sales' ? 'Can view and preview chatbots only' : newUserRole === 'customer' ? 'Can manage their assigned chatbot and email responder' : 'Full access to all features'}
                    </p>

                    {newUserRole === 'customer' && (
                      <div style={{ marginTop: '16px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '13px', color: 'var(--color-text-primary)' }}>
                          Assign to Chatbot
                        </label>
                        <select
                          value={newUserChatbotId}
                          onChange={(e) => setNewUserChatbotId(e.target.value)}
                          required
                          style={{
                            width: '100%',
                            padding: '12px 14px',
                            border: '2px solid var(--color-border)',
                            borderRadius: '8px',
                            fontSize: '14px',
                            background: 'var(--color-bg-white)',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            outline: 'none',
                            fontFamily: 'inherit'
                          }}
                        >
                          <option value="">Select a chatbot...</option>
                          {chatbotList.map((cb) => (
                            <option key={cb.id} value={cb.id}>{cb.name}</option>
                          ))}
                        </select>
                        <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)', margin: '8px 0 0 0' }}>
                          This customer will only be able to access and manage this chatbot.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', paddingTop: '8px' }}>
                  <button
                    type="submit"
                    disabled={creatingUser}
                    style={{
                      flex: 1,
                      padding: '12px 24px',
                      background: creatingUser ? 'var(--color-secondary)' : 'var(--color-success)',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: creatingUser ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      if (!creatingUser) {
                        e.currentTarget.style.background = 'var(--color-success-hover)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!creatingUser) {
                        e.currentTarget.style.background = 'var(--color-success)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    {creatingUser && (
                      <svg style={{ animation: 'spin 1s linear infinite' }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" strokeLinecap="round"/>
                      </svg>
                    )}
                    {creatingUser ? (inviteMode ? 'Sending Invite...' : 'Creating User...') : (inviteMode ? 'Send Invite' : 'Create User')}
                  </button>
                  <button
                    type="button"
                    onClick={closeCreateUserForm}
                    style={{
                      padding: '12px 24px',
                      background: 'var(--color-bg-white)',
                      border: '2px solid var(--color-border)',
                      borderRadius: '8px',
                      color: 'var(--color-text-secondary)',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--color-bg-secondary)';
                      e.currentTarget.style.borderColor = 'var(--color-border)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'var(--color-bg-white)';
                      e.currentTarget.style.borderColor = 'var(--color-border)';
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
              )}
            </div>
          )}

          <p style={{ color: 'var(--color-text-secondary)', marginBottom: '16px', fontSize: '14px' }}>
            Manage user accounts and their access levels.
          </p>

          {/* Reset Password Modal */}
          {resetPasswordUserId && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '20px'
            }}>
              <div style={{
                background: 'var(--color-bg-white)',
                padding: '32px',
                borderRadius: '12px',
                maxWidth: '500px',
                width: '100%',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
              }}>
                <h3 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--color-text-primary)', margin: '0 0 8px 0' }}>
                  Reset Password
                </h3>
                <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', margin: '0 0 24px 0' }}>
                  Set a new password for <strong>{resetPasswordEmail}</strong>
                </p>

                <form onSubmit={handleResetPassword}>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '13px', color: 'var(--color-text-primary)' }}>
                      New Password
                    </label>
                    <input
                      type="password"
                      value={resetPasswordValue}
                      onChange={(e) => {
                        setResetPasswordValue(e.target.value);
                        setResetPasswordValidation(validatePasswordStrength(e.target.value));
                      }}
                      placeholder="••••••••••••"
                      required
                      minLength={12}
                      autoFocus
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        border: '2px solid var(--color-border)',
                        borderRadius: '8px',
                        fontSize: '14px',
                        transition: 'all 0.2s',
                        outline: 'none',
                        fontFamily: 'inherit',
                        letterSpacing: '0.05em'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = 'var(--color-primary)';
                        e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--color-border)';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                    <div style={{
                      marginTop: '10px',
                      padding: '14px',
                      background: 'linear-gradient(to bottom, var(--color-bg-secondary), var(--color-bg-secondary))',
                      borderRadius: '8px',
                      border: '1px solid var(--color-border)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="12" y1="16" x2="12" y2="12"/>
                          <line x1="12" y1="8" x2="12.01" y2="8"/>
                        </svg>
                        <p style={{ fontSize: '13px', color: 'var(--color-text-primary)', margin: 0, fontWeight: '600' }}>
                          Password Requirements
                        </p>
                      </div>
                      <div className="grid-2col" style={{ gap: '8px', fontSize: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {resetPasswordValidation.length ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" />
                            </svg>
                          )}
                          <span style={{ color: resetPasswordValidation.length ? 'var(--color-success)' : 'var(--color-text-muted)' }}>12+ characters</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {resetPasswordValidation.number ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" />
                            </svg>
                          )}
                          <span style={{ color: resetPasswordValidation.number ? 'var(--color-success)' : 'var(--color-text-muted)' }}>One number</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {resetPasswordValidation.uppercase ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" />
                            </svg>
                          )}
                          <span style={{ color: resetPasswordValidation.uppercase ? 'var(--color-success)' : 'var(--color-text-muted)' }}>One uppercase</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {resetPasswordValidation.special ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" />
                            </svg>
                          )}
                          <span style={{ color: resetPasswordValidation.special ? 'var(--color-success)' : 'var(--color-text-muted)' }}>One special (!@#-)</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {resetPasswordValidation.lowercase ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" />
                            </svg>
                          )}
                          <span style={{ color: resetPasswordValidation.lowercase ? 'var(--color-success)' : 'var(--color-text-muted)' }}>One lowercase</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      type="submit"
                      disabled={resettingPassword}
                      style={{
                        flex: 1,
                        padding: '12px 24px',
                        background: resettingPassword ? 'var(--color-secondary)' : 'var(--color-primary)',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: resettingPassword ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      {resettingPassword && (
                        <svg style={{ animation: 'spin 1s linear infinite' }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" strokeLinecap="round"/>
                        </svg>
                      )}
                      {resettingPassword ? 'Resetting...' : 'Reset Password'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setResetPasswordUserId(null);
                        setResetPasswordEmail('');
                        setResetPasswordValue('');
                        setResetPasswordValidation(emptyValidation);
                      }}
                      style={{
                        padding: '12px 24px',
                        background: 'var(--color-bg-white)',
                        border: '2px solid var(--color-border)',
                        borderRadius: '8px',
                        color: 'var(--color-text-secondary)',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {teamUsers.length > 0 && (
            <>
              <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--color-text-primary)', margin: '16px 0 8px' }}>
                Team Members
                <span style={{ marginLeft: '8px', fontSize: '13px', fontWeight: 400, color: 'var(--color-text-muted)' }}>({teamUsers.length})</span>
              </h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: 'var(--color-text-secondary)' }}>Email</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: 'var(--color-text-secondary)' }}>Role</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: 'var(--color-text-secondary)' }}>Created</th>
                      <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: 'var(--color-text-secondary)' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamUsers.map((u) => {
                      const isCurrentUser = u.email === user?.email;
                      const canModify = !isCurrentUser && u.role !== 'admin';
                      return (
                        <tr key={u.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                          <td style={{ padding: '12px', fontSize: '14px' }}>
                            {u.email}
                            {isCurrentUser && (
                              <span style={{ marginLeft: '8px', padding: '2px 8px', background: 'var(--color-primary-light)', color: 'var(--color-primary-text)', borderRadius: '4px', fontSize: '12px', fontWeight: 500 }}>You</span>
                            )}
                          </td>
                          <td style={{ padding: '12px', fontSize: '14px' }}>
                            <span style={{
                              padding: '4px 12px',
                              background: u.role === 'admin' ? 'var(--color-danger-light)' : 'var(--color-primary-light)',
                              color: u.role === 'admin' ? 'var(--color-danger)' : 'var(--color-primary-text)',
                              borderRadius: '12px', fontSize: '13px', fontWeight: 500,
                            }}>{u.role}</span>
                          </td>
                          <td style={{ padding: '12px', fontSize: '14px', color: 'var(--color-text-secondary)' }}>{new Date(u.created_at).toLocaleDateString()}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>
                            {canModify ? (
                              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                <button onClick={() => { setResetPasswordUserId(u.id); setResetPasswordEmail(u.email); }}
                                  style={{ padding: '6px 12px', background: 'var(--color-primary-light)', border: '1px solid var(--color-primary)', borderRadius: '6px', color: 'var(--color-primary)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s' }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-primary)'; e.currentTarget.style.color = 'white'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-primary-light)'; e.currentTarget.style.color = 'var(--color-primary)'; }}
                                  title="Reset password">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px' }}>
                                    <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                                  </svg>
                                  Reset Password
                                </button>
                                <button onClick={() => handleDeleteUser(u.id, u.email, u.role)}
                                  style={{ padding: '6px 12px', background: 'var(--color-danger-light)', border: '1px solid var(--color-danger)', borderRadius: '6px', color: 'var(--color-danger)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s' }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-danger)'; e.currentTarget.style.color = 'white'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-danger-light)'; e.currentTarget.style.color = 'var(--color-danger)'; }}
                                  title="Delete user">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px' }}>
                                    <polyline points="3 6 5 6 21 6"/>
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                  </svg>
                                  Delete
                                </button>
                              </div>
                            ) : (
                              <span style={{ fontSize: '13px', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>{isCurrentUser ? 'Current user' : 'Protected'}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {customerUsers.length > 0 && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '24px 0 8px' }}>
                <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                  Customer Accounts
                  <span style={{ marginLeft: '8px', fontSize: '13px', fontWeight: 400, color: 'var(--color-text-muted)' }}>({customerUsers.length})</span>
                </h3>
                <input
                  type="text"
                  placeholder="Search by email or chatbot..."
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  style={{ padding: '6px 12px', border: '1px solid var(--color-border)', borderRadius: '6px', fontSize: '13px', width: '240px', outline: 'none', background: 'var(--color-bg-white)', color: 'var(--color-text-primary)' }}
                />
              </div>

              {orphanedCustomers.length > 0 && (
                <div style={{ background: 'var(--color-warning-light)', border: '1px solid var(--color-warning-border)', borderRadius: '8px', marginBottom: '12px', overflow: 'hidden' }}>
                  <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid var(--color-warning-border)' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-warning)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-badge-warning-text)' }}>
                      {orphanedCustomers.length} Orphaned Customer{orphanedCustomers.length !== 1 ? 's' : ''} (chatbot deleted)
                    </span>
                  </div>
                  {orphanedCustomers.map(u => (
                    <div key={u.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid var(--color-border)' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: '14px', color: 'var(--color-text-primary)' }}>{u.email}</span>
                        <span style={{ marginLeft: '12px', fontSize: '12px', color: 'var(--color-text-muted)' }}>{new Date(u.created_at).toLocaleDateString()}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                        <button onClick={() => { setResetPasswordUserId(u.id); setResetPasswordEmail(u.email); }}
                          style={{ padding: '4px 10px', background: 'var(--color-primary-light)', border: '1px solid var(--color-primary)', borderRadius: '6px', color: 'var(--color-primary)', fontSize: '12px', fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s' }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-primary)'; e.currentTarget.style.color = 'white'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-primary-light)'; e.currentTarget.style.color = 'var(--color-primary)'; }}
                          title="Reset password">Reset Password</button>
                        <button onClick={() => handleDeleteUser(u.id, u.email, u.role)}
                          style={{ padding: '4px 10px', background: 'var(--color-danger-light)', border: '1px solid var(--color-danger)', borderRadius: '6px', color: 'var(--color-danger)', fontSize: '12px', fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s' }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-danger)'; e.currentTarget.style.color = 'white'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-danger-light)'; e.currentTarget.style.color = 'var(--color-danger)'; }}
                          title="Delete user">Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {sortedCustomerGroups.map(([chatbotId, group]) => {
                const isCollapsed = collapsedGroups.has(chatbotId);
                return (
                  <div key={chatbotId} style={{ border: '1px solid var(--color-border)', borderRadius: '8px', marginBottom: '8px', overflow: 'hidden' }}>
                    <button
                      onClick={() => setCollapsedGroups(prev => { const next = new Set(prev); if (next.has(chatbotId)) next.delete(chatbotId); else next.add(chatbotId); return next; })}
                      style={{ width: '100%', padding: '10px 16px', background: 'var(--color-bg-secondary)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 500, color: 'var(--color-text-primary)', textAlign: 'left' }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                        style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}>
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                      {group.chatbotName}
                      <span style={{ fontSize: '12px', fontWeight: 400, color: 'var(--color-text-muted)' }}>({group.customers.length})</span>
                    </button>
                    {!isCollapsed && group.customers.map(u => (
                      <div key={u.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid var(--color-border)' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ fontSize: '14px', color: 'var(--color-text-primary)' }}>{u.email}</span>
                          <span style={{ marginLeft: '12px', fontSize: '12px', color: 'var(--color-text-muted)' }}>{new Date(u.created_at).toLocaleDateString()}</span>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                          <button onClick={() => { setResetPasswordUserId(u.id); setResetPasswordEmail(u.email); }}
                            style={{ padding: '4px 10px', background: 'var(--color-primary-light)', border: '1px solid var(--color-primary)', borderRadius: '6px', color: 'var(--color-primary)', fontSize: '12px', fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-primary)'; e.currentTarget.style.color = 'white'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-primary-light)'; e.currentTarget.style.color = 'var(--color-primary)'; }}
                            title="Reset password">Reset Password</button>
                          <button onClick={() => handleDeleteUser(u.id, u.email, u.role)}
                            style={{ padding: '4px 10px', background: 'var(--color-danger-light)', border: '1px solid var(--color-danger)', borderRadius: '6px', color: 'var(--color-danger)', fontSize: '12px', fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-danger)'; e.currentTarget.style.color = 'white'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-danger-light)'; e.currentTarget.style.color = 'var(--color-danger)'; }}
                            title="Delete user">Delete</button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}

              {filteredCustomers.length === 0 && customerSearch && (
                <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', textAlign: 'center', padding: '24px 0' }}>No customers matching "{customerSearch}"</p>
              )}
            </>
          )}
        </div>

        {/* Test Email Section */}
        <div
          style={{
            backgroundColor: 'var(--color-bg-white)',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: 'var(--shadow-sm)',
            marginTop: '24px',
          }}
        >
          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
            Email Testing
          </h2>
          <p style={{ color: 'var(--color-text-secondary)', marginBottom: '24px', fontSize: '14px' }}>
            Send a test email to verify your email configuration is working correctly.
            Emails are sent from the configured Microsoft Graph sender address.
          </p>

          <button
            type="button"
            onClick={handleTestEmail}
            style={{
              padding: '10px 20px',
              backgroundColor: 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            Send Test Email
          </button>
        </div>

        {/* Data Retention Section */}
        {user?.role === 'admin' && <SalesReferenceAccessCard />}
        {user?.role === 'admin' && <DataRetentionCard />}

        {/* Database Information Section */}
        {dbInfo && (
          <div style={{
            background: 'var(--color-bg-white)',
            border: '1px solid var(--color-border)',
            borderRadius: '12px',
            padding: '24px',
            marginTop: '24px'
          }}>
            <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px', color: 'var(--color-text-primary)' }}>
              Database Information
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '20px' }}>
              Details about your Cloudflare D1 database instance location and current processing datacenter.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
              <div style={{
                padding: '16px',
                background: 'var(--color-bg-secondary)',
                borderRadius: '8px',
                border: '1px solid var(--color-border)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M3 9h18" />
                    <path d="M9 21V9" />
                  </svg>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>
                    Primary Location
                  </span>
                </div>
                <p style={{ fontSize: '16px', fontWeight: 600, color: 'var(--color-text-primary)', margin: 0 }}>
                  {dbInfo.primaryLocation}
                </p>
                <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '4px' }}>
                  D1 database home region
                </p>
              </div>

              <div style={{
                padding: '16px',
                background: 'var(--color-bg-secondary)',
                borderRadius: '8px',
                border: '1px solid var(--color-border)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M2 12h20" />
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>
                    Current Datacenter
                  </span>
                </div>
                <p style={{ fontSize: '16px', fontWeight: 600, color: 'var(--color-text-primary)', margin: 0 }}>
                  {dbInfo.currentDatacenter}
                </p>
                <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '4px' }}>
                  Cloudflare edge location (IATA code)
                </p>
              </div>
            </div>
          </div>
        )}
        {/* Analytics Dashboard */}
        {user?.role === 'admin' && <AnalyticsDashboard />}

        {/* Activity Log Section */}
        {user?.role === 'admin' && <ActivityLogSection chatbots={chatbotList} />}

        {/* Active Sessions Section */}
        <div style={{
          background: 'var(--color-bg-white)',
          border: '1px solid var(--color-border)',
          borderRadius: '12px',
          padding: '24px',
          marginTop: '24px'
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px', color: 'var(--color-text-primary)' }}>
            Active Sessions
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '20px' }}>
            Manage active login sessions for your account. You can revoke any session to log out that device immediately.
          </p>

          {sessions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-muted)' }}>
              <p>No active sessions found</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {sessions.map((session) => {
                const browser = session.user_agent.includes('Chrome') ? 'Chrome' :
                               session.user_agent.includes('Firefox') ? 'Firefox' :
                               session.user_agent.includes('Safari') ? 'Safari' :
                               session.user_agent.includes('Edge') ? 'Edge' : 'Browser';
                
                const os = session.user_agent.includes('Windows') ? 'Windows' :
                          session.user_agent.includes('Mac') ? 'macOS' :
                          session.user_agent.includes('Linux') ? 'Linux' :
                          session.user_agent.includes('iPhone') ? 'iOS' :
                          session.user_agent.includes('Android') ? 'Android' : 'Unknown';
                
                // Convert country code to full name
                let countryName = session.country;
                try {
                  if (session.country) {
                    countryName = new Intl.DisplayNames(['en'], { type: 'region' }).of(session.country) || session.country;
                  }
                } catch (e) {
                  countryName = session.country;
                }
                
                // Show "City, Country" or fallback to just one if available
                const location = [session.city, countryName].filter(Boolean).join(', ') || 'Unknown location';
                const ip = session.ip_address === 'unknown' ? 'Local development' : session.ip_address;
                const lastActive = new Date(session.last_active).toLocaleString();
                const created = new Date(session.created_at).toLocaleDateString();

                return (
                  <div
                    key={session.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '16px',
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px',
                      background: 'var(--color-bg-secondary)'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--color-text-secondary)' }}>
                          <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                          <line x1="8" y1="21" x2="16" y2="21" />
                          <line x1="12" y1="17" x2="12" y2="21" />
                        </svg>
                        <span style={{ fontWeight: 600, fontSize: '15px', color: 'var(--color-text-primary)' }}>
                          {browser} on {os}
                        </span>
                      </div>
                      <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)', paddingLeft: '26px' }}>
                        <div>{location} · {ip}</div>
                        <div style={{ marginTop: '4px' }}>
                          Last active: {lastActive} · Created: {created}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRevokeSession(session.id)}
                      style={{
                        padding: '8px 16px',
                        background: 'var(--color-bg-white)',
                        border: '1px solid var(--color-danger)',
                        borderRadius: '6px',
                        color: 'var(--color-danger)',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--color-danger)';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--color-bg-white)';
                        e.currentTarget.style.color = 'var(--color-danger)';
                      }}
                    >
                      Revoke
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </>)}

      </div>
      </div>
    </div>
  );
}
